import React from "react";
import {
  buildMerchantSession,
  MERCHANT_PORTAL_USER_KEY,
  clearStoredMerchantPortalSession,
  getStoredMerchantPortalToken,
  resolveMerchantPermissions,
  setStoredMerchantPortalToken,
} from "./merchantAuthSession";
import { fetchMerchantContext, isMerchantUnauthorizedError } from "../data/merchantApi";

const MERCHANT_PERMISSION_PATHS = {
  dashboard: "/dashboard",
  transactions: "/transactions",
  settlements: "/settlements",
  outlets: "/outlets",
  reports: "/reports",
  users: "/users",
  settings: "/settings",
};

const MerchantAuthStateContext = React.createContext();
const MerchantAuthActionsContext = React.createContext();
const MerchantAuthUserContext = React.createContext();
const MerchantAuthPermissionsContext = React.createContext();

function getInitialMerchantUser() {
  const merchantUser = localStorage.getItem(MERCHANT_PORTAL_USER_KEY);

  if (!merchantUser) {
    return null;
  }

  try {
    return JSON.parse(merchantUser);
  } catch (error) {
    return null;
  }
}

export function getMerchantDefaultRoute(user) {
  const permissions = resolveMerchantPermissions(user);

  for (let index = 0; index < permissions.length; index += 1) {
    const path = MERCHANT_PERMISSION_PATHS[permissions[index]];

    if (path) {
      return path;
    }
  }

  return "/dashboard";
}

export function getMerchantPermissionPath(permission) {
  return MERCHANT_PERMISSION_PATHS[permission] || "/dashboard";
}

function MerchantAuthProvider({ children }) {
  const [user, setUser] = React.useState(getInitialMerchantUser);
  const [isAuthenticated, setIsAuthenticated] = React.useState(
    Boolean(getStoredMerchantPortalToken()),
  );
  const [permissions, setPermissions] = React.useState(function getInitialPermissions() {
    return resolveMerchantPermissions(getInitialMerchantUser());
  });

  React.useEffect(function bindStorageListener() {
    function handleStorage(event) {
      if (event.key === "merchant_portal_token") {
        setIsAuthenticated(Boolean(event.newValue));
      }

      if (event.key === MERCHANT_PORTAL_USER_KEY) {
        try {
          const nextUser = event.newValue ? JSON.parse(event.newValue) : null;

          setUser(nextUser);
          setPermissions(resolveMerchantPermissions(nextUser));
        } catch (error) {
          setUser(null);
          setPermissions(resolveMerchantPermissions(null));
        }
      }
    }

    window.addEventListener("storage", handleStorage);

    return function cleanup() {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const commitSession = React.useCallback(function commitSession(session) {
    const normalizedSession = session || null;
    const token =
      normalizedSession && normalizedSession.jwtToken
        ? normalizedSession.jwtToken
        : normalizedSession && normalizedSession.accessToken
          ? normalizedSession.accessToken
          : "";

    if (token) {
      setStoredMerchantPortalToken(token);
      setIsAuthenticated(true);
    } else {
      setStoredMerchantPortalToken("");
      setIsAuthenticated(false);
    }

    if (normalizedSession) {
      localStorage.setItem(MERCHANT_PORTAL_USER_KEY, JSON.stringify(normalizedSession));
    } else {
      localStorage.removeItem(MERCHANT_PORTAL_USER_KEY);
    }

    setUser(normalizedSession);
    setPermissions(resolveMerchantPermissions(normalizedSession));
  }, []);

  const clearSession = React.useCallback(function clearSession() {
    clearStoredMerchantPortalSession();
    setIsAuthenticated(false);
    setUser(null);
    setPermissions(resolveMerchantPermissions(null));
  }, []);

  React.useEffect(
    function hydrateMerchantContext() {
      const storedToken = getStoredMerchantPortalToken();
      const storedUser = getInitialMerchantUser();

      if (!storedToken) {
        return undefined;
      }

      let isActive = true;

      async function loadMerchantContext() {
        try {
          const merchantContext = await fetchMerchantContext();

          if (!isActive) {
            return;
          }

          commitSession(
            buildMerchantSession({
              previousSession: storedUser,
              auth: {
                access_token: storedToken,
                token_type:
                  storedUser && storedUser.tokenType ? storedUser.tokenType : "bearer",
                expires_in:
                  storedUser && storedUser.expiresIn ? storedUser.expiresIn : 0,
              },
              context: merchantContext,
            }),
          );
        } catch (error) {
          if (!isActive) {
            return;
          }

          if (isMerchantUnauthorizedError(error)) {
            clearSession();
          }
        }
      }

      loadMerchantContext();

      return function cleanup() {
        isActive = false;
      };
    },
    [clearSession, commitSession],
  );

  const actions = React.useMemo(
    function createActions() {
      return {
        login(session) {
          commitSession(session);
        },
        logout() {
          clearSession();
        },
      };
    },
    [clearSession, commitSession],
  );

  return (
    <MerchantAuthStateContext.Provider value={isAuthenticated}>
      <MerchantAuthUserContext.Provider value={user}>
        <MerchantAuthPermissionsContext.Provider value={permissions}>
          <MerchantAuthActionsContext.Provider value={actions}>
            {children}
          </MerchantAuthActionsContext.Provider>
        </MerchantAuthPermissionsContext.Provider>
      </MerchantAuthUserContext.Provider>
    </MerchantAuthStateContext.Provider>
  );
}

function useMerchantAuthState() {
  const context = React.useContext(MerchantAuthStateContext);

  if (context === undefined) {
    throw new Error("useMerchantAuthState must be used within MerchantAuthProvider");
  }

  return context;
}

function useMerchantAuthActions() {
  const context = React.useContext(MerchantAuthActionsContext);

  if (context === undefined) {
    throw new Error("useMerchantAuthActions must be used within MerchantAuthProvider");
  }

  return context;
}

function useMerchantAuthUser() {
  const context = React.useContext(MerchantAuthUserContext);

  if (context === undefined) {
    throw new Error("useMerchantAuthUser must be used within MerchantAuthProvider");
  }

  return context;
}

function useMerchantPermissions() {
  const context = React.useContext(MerchantAuthPermissionsContext);

  if (context === undefined) {
    throw new Error("useMerchantPermissions must be used within MerchantAuthProvider");
  }

  return context;
}

function useMerchantHasPermission(permission) {
  const permissions = useMerchantPermissions();

  return permissions.indexOf(permission) >= 0;
}

export {
  MerchantAuthProvider,
  useMerchantAuthActions,
  useMerchantAuthState,
  useMerchantAuthUser,
  useMerchantHasPermission,
  useMerchantPermissions,
};
